import { jsPDF } from "jspdf";

const COLORS = {
  bg: [6, 7, 10],
  panel: [15, 17, 22],
  panel2: [19, 21, 27],
  text: [248, 250, 252],
  muted: [156, 163, 175],
  subtle: [107, 114, 128],
  blue: [59, 130, 246],
  green: [16, 185, 129],
  amber: [245, 158, 11],
  border: [38, 42, 50],
};

const CURRENCY_BY_COUNTRY = {
  India: ["INR", "₹"], UAE: ["AED", "AED "], "United Arab Emirates": ["AED", "AED "],
  USA: ["USD", "$"], "United States": ["USD", "$"], UK: ["GBP", "£"], "United Kingdom": ["GBP", "£"],
  "Saudi Arabia": ["SAR", "SAR "], Qatar: ["QAR", "QAR "], Kuwait: ["KWD", "KWD "], Bahrain: ["BHD", "BHD "],
  Oman: ["OMR", "OMR "], Canada: ["CAD", "C$"], Australia: ["AUD", "A$"], Singapore: ["SGD", "S$"],
  Germany: ["EUR", "€"], France: ["EUR", "€"], Netherlands: ["EUR", "€"], Ireland: ["EUR", "€"],
  "New Zealand": ["NZD", "NZ$"], "South Africa": ["ZAR", "R"], Malaysia: ["MYR", "RM"], Japan: ["JPY", "¥"],
};

const CURRENCY_BY_CODE = {
  INR: ["INR", "₹"], AED: ["AED", "AED "], USD: ["USD", "$"], GBP: ["GBP", "£"], SAR: ["SAR", "SAR "],
  QAR: ["QAR", "QAR "], KWD: ["KWD", "KWD "], BHD: ["BHD", "BHD "], OMR: ["OMR", "OMR "], CAD: ["CAD", "C$"],
  AUD: ["AUD", "A$"], SGD: ["SGD", "S$"], EUR: ["EUR", "€"], NZD: ["NZD", "NZ$"], ZAR: ["ZAR", "R"],
  MYR: ["MYR", "RM"], JPY: ["JPY", "¥"],
};

function cleanText(value) {
  return String(value ?? "")
    .replace(/[\u{1F300}-\u{1FAFF}]/gu, "")
    .replace(/[\u2600-\u27BF]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function currencyInfo(profile = {}) {
  const country = cleanText(profile.country);
  const code = cleanText(profile.currency).toUpperCase();
  return CURRENCY_BY_CODE[code] || CURRENCY_BY_COUNTRY[country] || ["USD", "$"];
}

function formatMoney(value, profile = {}) {
  if (value === null || value === undefined || value === "") return "—";
  const [code, symbol] = currencyInfo(profile);
  const raw = cleanText(value);
  if (new RegExp(`\\b${code}\\b`, "i").test(raw)) return raw;
  if (symbol.trim() && raw.startsWith(symbol.trim())) return raw;
  const numeric = Number(raw.replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(numeric)) return raw;
  return `${symbol}${numeric.toLocaleString("en-US")}`;
}

function numberValue(value) {
  if (typeof value === "number") return value;
  const n = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

export function downloadRoadmapPDF(roadmap, user) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // Deliberately conservative printable area. Never allow text to touch the page edge.
  const M = 50;
  const CONTENT_W = W - M * 2;
  const FOOTER_Y = H - 27;
  const BOTTOM = H - 58;

  const profile = user?.profile || {};
  const fullName = cleanText(user?.name || "Your Name");
  const [currencyCode] = currencyInfo(profile);
  let y = M;

  const setText = (color, size, weight = "normal") => {
    doc.setTextColor(...color);
    doc.setFontSize(size);
    doc.setFont("helvetica", weight);
  };

  // IMPORTANT: wrapping is calculated AFTER font/size is set.
  // The previous export calculated wrapping using whatever font happened to be active,
  // which caused long lines to run underneath the right edge/priority labels.
  const wrap = (value, width, size = 10, weight = "normal") => {
    setText(COLORS.text, size, weight);
    const text = cleanText(value) || "—";
    const safeWidth = Math.max(30, width);
    const words = text.split(" ");
    const lines = [];
    let line = "";

    const pushWord = (word) => {
      if (!word) return;
      const candidate = line ? `${line} ${word}` : word;
      if (doc.getTextWidth(candidate) <= safeWidth) {
        line = candidate;
        return;
      }
      if (line) lines.push(line);
      line = "";
      // Break exceptionally long tokens instead of letting them overflow.
      let chunk = "";
      for (const ch of word) {
        const next = chunk + ch;
        if (doc.getTextWidth(next) <= safeWidth || !chunk) chunk = next;
        else { lines.push(chunk); chunk = ch; }
      }
      line = chunk;
    };

    words.forEach(pushWord);
    if (line) lines.push(line);
    return lines.length ? lines : ["—"];
  };

  const lineHeight = (size, factor = 1.35) => size * factor;

  const fillPage = () => {
    doc.setFillColor(...COLORS.bg);
    doc.rect(0, 0, W, H, "F");
  };

  const newPage = () => {
    doc.addPage();
    fillPage();
    y = M + 8;
  };

  const ensure = (height) => {
    if (y + height > BOTTOM) newPage();
  };

  const card = (x, top, width, height, fill = COLORS.panel) => {
    doc.setFillColor(...fill);
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.7);
    doc.roundedRect(x, top, width, height, 8, 8, "FD");
  };

  const section = (eyebrow, title, subtitle = "") => {
    const titleLines = wrap(title, CONTENT_W, 19, "bold");
    const subLines = subtitle ? wrap(subtitle, CONTENT_W, 8.5, "normal") : [];
    ensure(18 + titleLines.length * 23 + subLines.length * 12 + 18);

    setText(COLORS.blue, 8.5, "bold");
    doc.text(`// ${eyebrow}`, M, y);
    y += 18;

    setText(COLORS.text, 19, "bold");
    titleLines.forEach((line) => { doc.text(line, M, y); y += 23; });

    if (subLines.length) {
      setText(COLORS.muted, 8.5);
      subLines.forEach((line) => { doc.text(line, M, y); y += 12; });
    }
    y += 9;
  };

  const write = (text, width = CONTENT_W, size = 9.5, color = COLORS.muted, weight = "normal", gap = 0) => {
    const lines = wrap(text || "—", width, size, weight);
    const step = lineHeight(size);
    ensure(lines.length * step + gap);
    setText(color, size, weight);
    lines.forEach((line) => { doc.text(line, M, y); y += step; });
    y += gap;
  };

  const drawFooter = () => {
    doc.setDrawColor(...COLORS.border);
    doc.setLineWidth(0.5);
    doc.line(M, FOOTER_Y - 9, W - M, FOOTER_Y - 9);
    setText(COLORS.subtle, 7.5);
    const page = doc.internal.getCurrentPageInfo().pageNumber;
    const total = doc.internal.getNumberOfPages();
    doc.text("CAREER GPS AI", M, FOOTER_Y + 3);
    doc.text(`${page} / ${total}`, W / 2, FOOTER_Y + 3, { align: "center" });
    doc.text(fullName, W - M, FOOTER_Y + 3, { align: "right" });
  };

  const drawPageLabel = (label) => {
    setText(COLORS.subtle, 7.5, "bold");
    doc.text(label, W - M, M - 12, { align: "right" });
  };

  // COVER
  fillPage();
  y = 112;
  setText(COLORS.blue, 9, "bold");
  doc.text("CAREER GPS AI", M, y);
  y += 15;
  setText(COLORS.subtle, 7.5, "bold");
  doc.text("PERSONAL CAREER ROADMAP", M, y);
  y += 48;

  const nameLines = wrap(fullName, CONTENT_W, 34, "bold");
  setText(COLORS.text, 34, "bold");
  nameLines.forEach((line) => { doc.text(line, M, y); y += 39; });
  y += 4;

  const roleLine = `${cleanText(profile.current_job || "Current role")}  →  ${cleanText(profile.desired_career || "Dream career")}`;
  const roleLines = wrap(roleLine, CONTENT_W, 11, "normal");
  setText(COLORS.muted, 11);
  roleLines.forEach((line) => { doc.text(line, M, y); y += 16; });
  y += 34;

  const stats = [
    ["TIME TO GOAL", `${roadmap?.estimated_months_to_goal ?? "—"} months`],
    ["STUDY / DAY", `${profile.study_hours_per_day || 0} hours`],
    ["TARGET SALARY", formatMoney(profile.desired_salary, profile)],
  ];
  const gap = 10;
  const sw = (CONTENT_W - gap * 2) / 3;
  stats.forEach(([label, value], i) => {
    const x = M + i * (sw + gap);
    card(x, y, sw, 82);
    setText(COLORS.subtle, 7, "bold");
    doc.text(label, x + 13, y + 20);
    const valueLines = wrap(value, sw - 26, value.length > 14 ? 15 : 19, "bold").slice(0, 2);
    setText(COLORS.blue, value.length > 14 ? 15 : 19, "bold");
    valueLines.forEach((line, li) => doc.text(line, x + 13, y + 48 + li * 17));
  });
  y += 116;

  card(M, y, CONTENT_W, 58, COLORS.panel2);
  setText(COLORS.muted, 8.5);
  doc.text(`Currency  ${currencyCode}`, M + 15, y + 23);
  doc.text(`Generated  ${new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`, M + 15, y + 39);
  y += 96;
  setText(COLORS.subtle, 8);
  doc.text("Your roadmap is designed to turn today's skill gaps into measurable career progress.", M, y);

  // CAREER GAP
  newPage();
  drawPageLabel("01  ·  CAREER GAP");
  section("CAREER GAP ANALYSIS", "Where you are vs where you're going", "The current position, target role and practical bridge between them.");
  write(roadmap.career_gap_analysis || "No career gap analysis available.", CONTENT_W, 10, COLORS.muted, "normal", 15);

  if (roadmap.salary_estimate && typeof roadmap.salary_estimate === "object") {
    section("SALARY TRAJECTORY", "Expected salary progression");
    const entries = Object.entries(roadmap.salary_estimate);
    const gap2 = 10;
    const cw = (CONTENT_W - gap2) / 2;
    entries.forEach(([key, value], index) => {
      const col = index % 2;
      if (col === 0) ensure(62);
      const rowY = y;
      const x = M + col * (cw + gap2);
      card(x, rowY, cw, 54);
      setText(COLORS.subtle, 7.5, "bold");
      doc.text(cleanText(key).toUpperCase(), x + 12, rowY + 17);
      const display = numberValue(value) !== null ? formatMoney(numberValue(value), profile) : cleanText(value);
      const displayLines = wrap(display, cw - 24, display.length > 20 ? 8.5 : 10.5, "bold").slice(0, 2);
      setText(COLORS.green, display.length > 20 ? 8.5 : 10.5, "bold");
      displayLines.forEach((line, li) => doc.text(line, x + 12, rowY + 36 + li * 11));
      if (col === 1 || index === entries.length - 1) y = rowY + 64;
    });
  }

  // SKILLS — priority has a genuinely reserved column; name can never occupy it.
  if (Array.isArray(roadmap.skill_gaps) && roadmap.skill_gaps.length) {
    section("SKILL GAP ANALYSIS", "Skills to build", "Prioritized gaps between your current level and target capability.");

    roadmap.skill_gaps.forEach((skill) => {
      const name = cleanText(skill?.skill || "Skill");
      const current = cleanText(skill?.current_level || "—");
      const target = cleanText(skill?.target_level || "—");
      const priority = cleanText(skill?.priority || "—").toUpperCase();

      // 96pt reserved right-side priority column + generous inner padding.
      const innerLeft = M + 14;
      const innerRight = W - M - 14;
      const priorityW = 78;
      const nameW = CONTENT_W - 28 - priorityW - 8;
      const nameLines = wrap(name, nameW, 9.5, "bold");
      const level = `${current}  →  ${target}`;
      const levelLines = wrap(level, nameW, 8, "normal");
      const rowH = Math.max(52, nameLines.length * 13 + levelLines.length * 11 + 25);

      ensure(rowH + 8);
      const top = y;
      card(M, top, CONTENT_W, rowH);

      // Skill name column — never enters the priority column.
      setText(COLORS.text, 9.5, "bold");
      nameLines.forEach((line, i) => doc.text(line, innerLeft, top + 18 + i * 13));

      // Current → target level below the skill title.
      setText(COLORS.muted, 8);
      levelLines.forEach((line, i) => doc.text(line, innerLeft, top + rowH - 11 - (levelLines.length - 1 - i) * 11));

      // Priority sits in its own right column with a fixed anchor.
      const pc = priority === "HIGH" ? COLORS.amber : priority === "MEDIUM" ? COLORS.blue : COLORS.muted;
      setText(pc, 7.5, "bold");
      doc.text(priority, innerRight, top + 18, { align: "right" });

      y += rowH + 8;
    });
  }

  // LEARNING ROADMAP
  if (Array.isArray(roadmap.learning_roadmap) && roadmap.learning_roadmap.length) {
    newPage();
    drawPageLabel("02  ·  LEARNING ROADMAP");
    section("LEARNING ROADMAP", "Your step-by-step path", "Stages from foundation to career readiness.");

    roadmap.learning_roadmap.forEach((phase, index) => {
      const phaseName = cleanText(phase?.phase || `Phase ${index + 1}`);
      const duration = cleanText(phase?.duration_weeks ?? "—");
      const topics = Array.isArray(phase?.topics) ? phase.topics.map(cleanText).filter(Boolean) : [];
      const milestone = cleanText(phase?.milestone || "");
      const phaseLines = wrap(phaseName, CONTENT_W - 30, 11.5, "bold");
      const topicHeights = topics.map((t) => Math.max(1, wrap(t, CONTENT_W - 48, 8.5).length) * 12 + 3);
      const milestoneHeight = milestone ? Math.max(1, wrap(`Milestone: ${milestone}`, CONTENT_W - 48, 8.5, "bold").length) * 12 + 5 : 0;
      const cardH = 46 + phaseLines.length * 14 + topicHeights.reduce((a, b) => a + b, 0) + milestoneHeight + 12;

      if (cardH > BOTTOM - (M + 8)) newPage();
      else ensure(cardH + 8);

      card(M, y, CONTENT_W, cardH);
      setText(COLORS.blue, 7.5, "bold");
      doc.text(`PHASE ${index + 1}   ·   ${duration} WEEKS`, M + 13, y + 17);
      setText(COLORS.text, 11.5, "bold");
      phaseLines.forEach((line, i) => doc.text(line, M + 13, y + 34 + i * 14));

      let py = y + 37 + phaseLines.length * 14;
      setText(COLORS.muted, 8.5);
      topics.forEach((topic) => {
        const lines = wrap(topic, CONTENT_W - 48, 8.5);
        lines.forEach((line, i) => { doc.text(i === 0 ? `• ${line}` : `  ${line}`, M + 17, py); py += 12; });
        py += 3;
      });
      if (milestone) {
        setText(COLORS.green, 8.5, "bold");
        wrap(`Milestone: ${milestone}`, CONTENT_W - 48, 8.5, "bold").forEach((line) => { doc.text(line, M + 17, py); py += 12; });
      }
      y += cardH + 8;
    });
  }

  // WEEKLY PLAN
  if (Array.isArray(roadmap.weekly_study_plan) && roadmap.weekly_study_plan.length) {
    newPage();
    drawPageLabel("03  ·  WEEKLY PLAN");
    section("WEEKLY STUDY PLAN", "Your weekly learning rhythm", "A practical schedule that balances study, application and recovery.");

    const headerH = 25;
    card(M, y, CONTENT_W, headerH, COLORS.panel2);
    setText(COLORS.subtle, 7.5, "bold");
    doc.text("DAY", M + 12, y + 16);
    doc.text("TIME", M + 88, y + 16);
    doc.text("FOCUS", M + 132, y + 16);
    y += headerH + 4;

    roadmap.weekly_study_plan.forEach((day) => {
      const dayName = cleanText(day?.day || "Day");
      const hours = `${day?.hours ?? 0}h`;
      const focus = cleanText(day?.focus || "Rest / Recovery");
      const focusLines = wrap(focus, CONTENT_W - 145, 8.5);
      const rowH = Math.max(31, focusLines.length * 12 + 13);
      ensure(rowH + 4);
      card(M, y, CONTENT_W, rowH);
      setText(COLORS.text, 8.5, "bold");
      doc.text(dayName, M + 12, y + 18);
      setText(COLORS.blue, 8.5, "bold");
      doc.text(hours, M + 88, y + 18);
      setText(COLORS.muted, 8.5);
      focusLines.forEach((line, i) => doc.text(line, M + 132, y + 18 + i * 12));
      y += rowH + 4;
    });
  }

  // MONTHLY MILESTONES
  if (Array.isArray(roadmap.monthly_milestones) && roadmap.monthly_milestones.length) {
    newPage();
    drawPageLabel("04  ·  MILESTONES");
    section("MONTHLY MILESTONES", "Track progress month by month", "Concrete checkpoints to keep the roadmap moving forward.");

    roadmap.monthly_milestones.forEach((item) => {
      const month = cleanText(item?.month || "—");
      const milestone = cleanText(item?.milestone || "—");
      const textW = CONTENT_W - 110;
      const lines = wrap(milestone, textW, 8.8);
      const rowH = Math.max(43, lines.length * 13 + 17);
      ensure(rowH + 7);
      card(M, y, CONTENT_W, rowH);
      setText(COLORS.amber, 7.5, "bold");
      doc.text(`MONTH ${month}`, M + 12, y + 18);
      setText(COLORS.text, 8.8);
      lines.forEach((line, i) => doc.text(line, M + 94, y + 18 + i * 13));
      y += rowH + 7;
    });
  }

  // RESOURCES
  const certs = Array.isArray(roadmap.recommended_certifications) ? roadmap.recommended_certifications : [];
  const courses = Array.isArray(roadmap.recommended_youtube_courses) ? roadmap.recommended_youtube_courses : [];
  if (certs.length || courses.length) {
    newPage();
    drawPageLabel("05  ·  RESOURCES");
    section("FREE RESOURCES", "Recommended next resources", "Useful certifications and learning material to support the roadmap.");

    if (certs.length) {
      setText(COLORS.text, 12, "bold");
      doc.text("Recommended certifications", M, y);
      y += 21;
      certs.forEach((cert) => {
        const lines = wrap(cert, CONTENT_W - 22, 9);
        const h = lines.length * 13 + 8;
        ensure(h + 4);
        setText(COLORS.blue, 9);
        doc.text("•", M + 2, y);
        setText(COLORS.muted, 9);
        lines.forEach((line, i) => doc.text(line, M + 14, y + i * 13));
        y += h;
      });
      y += 8;
    }

    if (courses.length) {
      ensure(48);
      setText(COLORS.text, 12, "bold");
      doc.text("YouTube learning", M, y);
      y += 21;
      courses.forEach((course) => {
        const title = cleanText(course?.title || "Untitled course");
        const channel = cleanText(course?.channel || "");
        const url = cleanText(course?.url || course?.video_url || "");
        const titleLines = wrap(title, CONTENT_W, 9.5, "bold");
        const channelLines = channel ? wrap(channel, CONTENT_W, 8) : [];
        const urlLines = url ? wrap(url, CONTENT_W, 7) : [];
        const h = titleLines.length * 13 + channelLines.length * 11 + urlLines.length * 10 + 12;
        ensure(h + 4);
        setText(COLORS.text, 9.5, "bold");
        titleLines.forEach((line, i) => doc.text(line, M, y + i * 13));
        y += titleLines.length * 13;
        if (channelLines.length) {
          setText(COLORS.muted, 8);
          channelLines.forEach((line, i) => doc.text(line, M, y + i * 11));
          y += channelLines.length * 11;
        }
        if (urlLines.length) {
          setText(COLORS.blue, 7);
          urlLines.forEach((line, i) => doc.text(line, M, y + i * 10));
          y += urlLines.length * 10;
        }
        y += 12;
      });
    }
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    drawFooter();
  }

  const safeName = fullName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "user";
  doc.save(`career-roadmap-${safeName}-${currencyCode.toLowerCase()}.pdf`);
}
